import { BtnPrimary } from '../Buttons/BtnPrimary';
import { BtnSecondary } from '../Buttons/BtnSecondary';
import Skeleton from 'react-loading-skeleton';
import { CircleAlert, CircleCheck, Clock3, LockKeyhole } from 'lucide-react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { PaymentFeedbackPopup } from '../Popup/PaymentFeedbackPopup/PaymentFeedbackPopup';
import styles from './style.module.css';

const PaymentStatus = ({
  complete,
  reference,
  cardStored,
  loading,
  error,
  sessionExpired,
  activeMethod,
  attemptKey = activeMethod,
  failedAttemptsByMethod,
  lockExpiries,
  initiateCheckoutSession,
  handleNewSession,
  handleStoredCardPayment,
  handleSwitchMethod,
  selectedStoredCard,
  useModal = false,
  canSwitchMethod = true,
  authenticationBlocked = false,
  errorTitle,
  allowRetry = true,
  retryLabel = "Try Again",
  orderReference,
}) => {
  const titleId = useId();
  const [modalDismissed, setModalDismissed] = useState(false);
  const [redirectSeconds, setRedirectSeconds] = useState(5);
  const redirectTimerRef = useRef(null);
  const hasRedirectedRef = useRef(false);
  const newSessionHandlerRef = useRef(handleNewSession);
  newSessionHandlerRef.current = handleNewSession;
  const shouldAutoRedirect = useModal && sessionExpired && !authenticationBlocked && Boolean(error) && !complete && !loading;

  const returnToBilling = useCallback(() => {
    if (hasRedirectedRef.current) return;
    hasRedirectedRef.current = true;
    clearInterval(redirectTimerRef.current);
    newSessionHandlerRef.current?.();
  }, []);

  useEffect(() => {
    if (!shouldAutoRedirect) return undefined;
    hasRedirectedRef.current = false;
    let secondsRemaining = 5;
    setRedirectSeconds(secondsRemaining);
    redirectTimerRef.current = setInterval(() => {
      secondsRemaining -= 1;
      setRedirectSeconds(secondsRemaining);
      if (secondsRemaining === 0) returnToBilling();
    }, 1000);
    return () => clearInterval(redirectTimerRef.current);
  }, [shouldAutoRedirect, returnToBilling]);

  useEffect(() => {
    setModalDismissed(false);
  }, [error]);
  if (complete) {
    return (
      <div className={styles.errorCard} role="status" aria-labelledby={titleId}>
        <div className={styles.statusHeader}>
          <span className={`${styles.errorIcon} ${styles.successIcon}`}>
            <CircleCheck size={28} strokeWidth={1.75} aria-hidden="true" />
          </span>
          <div className={styles.statusContent}>
            <h3 id={titleId} className={styles.errorTitle}>Payment successful</h3>
            {reference && <p className={styles.errorMessage}>Reference: {reference}</p>}
            {cardStored && <p className={styles.errorMessage}>Card successfully saved.</p>}
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={styles.loadingSkeletonWrap} aria-busy="true">
        <Skeleton height={20} width={260} style={{ marginBottom: 16 }} />
        <Skeleton height={48} count={3} style={{ marginBottom: 10 }} />
      </div>
    );
  }

  if (error) {
    const isLocked =
      attemptKey &&
      (failedAttemptsByMethod[attemptKey] || 0) >= 5 &&
      Date.now() < (lockExpiries[attemptKey] || 0);

    const title = authenticationBlocked
      ? "Bank verification unavailable"
      : sessionExpired
      ? "Session expired"
      : isLocked
      ? "Payment method locked"
      : errorTitle || "Payment unsuccessful";
    const StatusIcon = sessionExpired ? Clock3 : isLocked ? LockKeyhole : CircleAlert;

    const feedback = (
      <div
        className={`${styles.errorCard} ${useModal && !modalDismissed ? styles.modalFeedback : ''}`}
        role="alert"
        aria-labelledby={titleId}
      >
        <div className={styles.statusHeader}>
          <span className={styles.errorIcon} data-state={sessionExpired || isLocked ? 'session' : 'error'}>
            <StatusIcon size={28} strokeWidth={1.75} aria-hidden="true" />
          </span>
          <div className={styles.statusContent}>
            <h3 id={titleId} className={styles.errorTitle}>{title}</h3>
            <p className={styles.errorMessage}>{error}</p>
            {!allowRetry && orderReference && (
              <p className={styles.errorHint}>
                Order reference: {orderReference}<br />
                <a className="underline" href="mailto:info@natureland.com.sg">Contact Natureland</a>
              </p>
            )}
            {sessionExpired && (
              <p className={styles.errorHint} role="status" aria-live="polite">
                {shouldAutoRedirect
                  ? redirectSeconds > 0
                    ? `Redirecting to billing details in ${redirectSeconds} ${redirectSeconds === 1 ? 'second' : 'seconds'}…`
                    : 'Redirecting to billing details…'
                  : 'Return to billing details to start a new payment session.'}
              </p>
            )}
          </div>
        </div>
        <div className={styles.errorActions}>
          {authenticationBlocked ? (
            <>
              {canSwitchMethod && <BtnPrimary label="Switch Payment Method" handleClick={handleSwitchMethod} noIcon />}
              <BtnSecondary type="button" label="Back to Billing Details" handleClick={handleNewSession} />
            </>
          ) : sessionExpired ? (
            /* Redirecting state: no Try Again — the flow restarts from billing details. */
            <BtnPrimary label="Back to Billing Details" handleClick={shouldAutoRedirect ? returnToBilling : handleNewSession} noIcon />
          ) : (
            /* Not redirecting: always offer Try Again. Retrying an order that was
               actually paid is safe — payment initiate returns 409 "already paid"
               and the shopper is sent to the homepage instead of charged twice. */
            <>
              <BtnPrimary
                label={retryLabel}
                handleClick={initiateCheckoutSession}
                disabled={isLocked}
                nativeDisabled
                noIcon
              />
              {!allowRetry ? (
                <BtnSecondary type="button" label="Back to billing details" handleClick={handleNewSession} />
              ) : (
                canSwitchMethod && (
                  <BtnSecondary
                    type="button"
                    label="Choose another payment method"
                    handleClick={handleSwitchMethod}
                  />
                )
              )}
            </>
          )}
        </div>
      </div>
    );
    // Keep recovery actions available on the page after dismissal. Closing the
    // modal must never navigate, recreate a session, or submit another payment.
    return useModal && !modalDismissed ? (
      <PaymentFeedbackPopup
        onClose={() => setModalDismissed(true)}
        showCloseButton={allowRetry && !sessionExpired && !authenticationBlocked && !canSwitchMethod}
      >
        {feedback}
      </PaymentFeedbackPopup>
    ) : feedback;
  }

  return null;
};

export default PaymentStatus;
